import React, {useState, useEffect, useRef, useContext} from 'react';

import {Button} from "@progress/kendo-react-buttons";
import {Popover, Tooltip} from "@progress/kendo-react-tooltip";
import {Input, TextArea} from "@progress/kendo-react-inputs";
import {Label} from "@progress/kendo-react-labels";
import {useLocalization} from "@progress/kendo-react-intl";
import {AutoComplete} from "@progress/kendo-react-dropdowns";
import {filterBy} from "@progress/kendo-data-query";
import {Upload} from "@progress/kendo-react-upload";

import {AnchorWrap, Text} from "./MultilingualText";
import {
    mainMessages,
    requestTechnicalSupportKey,
    supportContactInformationKey,
    supportDescriptionKey,
    supportDescriptionPlaceholderKey,
    supportEnterProductNumberKey,
    supportSubjectKey,
    supportSubjectPlaceholderKey,
    supportEnterProductNumberPlaceholderKey,
    supportAttachmentKey,
    supportAttachmentButtonKey,
    supportAttachmentDescriptionKey,
    submitRequestKey,
    supportErrorMessageKey,
    supportSuccessMessageOneKey,
    supportSuccessMessageTwoKey,
    supportSuccessMessageThreeKey,
    supportErrorTitleKey,
    supportSuccessTitleKey,
    supportSuccessTitleTwoKey,
} from "../../assets/text/MultilingualText";
import "../../assets/css/FloatingSupportButton.css";
import UserContext from "./UserContext";
import config from '../../config.js';
import {CloseIcon, HeadsetIcon, InfoIcon} from "./icons";
import axios from "axios";
import {useLocation} from "react-router-dom";
import Alert from "./Alert";
import {Loader} from "@progress/kendo-react-indicators";
import {useSupportContext} from "./SupportContext";


export default function FloatingSupportButton(props) {

    const {
        accessToken,
        siteLanguageDefault,
        mainMenu,
    } = useContext(UserContext);
    const { state } = useSupportContext();
    const localization = useLocalization();
    let location = useLocation();

    const [isLoading, setIsLoading] = useState(false);
    const anchor = useRef(null);
    const contentRef = React.useRef();
    // const blurTimeoutRef = React.useRef();
    const [showForm, setShowForm] = useState(false);
    const [submitDisabled, setSubmitDisabled] = useState(true);
    const [submitError, setSubmitError] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [submitCaseId, setSubmitCaseId] = useState('');

    const [productsList, setProductsList] = useState([]);
    const [productState, setProductState] = useState({
        data: productsList,
        value: ''
    });

    const [formValues, setFormValues] = useState({
        'subject': '',
        'description': '',
    });
    const [file, setFile] = useState("");

    const onChangeForm = (e) => {
        const name = e.target.element.name || e.target.name
        formValues[name] = e.value
        setFormValues({...formValues})
    }

    // Handle disabling of submit button
    useEffect(() => {
        if (formValues?.subject.trim() && formValues?.description.trim()) setSubmitDisabled(false);
        else setSubmitDisabled(true);
    }, [formValues]);

    // API to send ticket request to backend
    const submitTicket = () => {
        setIsLoading(true);
        setSubmitSuccess(false);
        setSubmitError(false);
        setSubmitCaseId('');
        let headers = {
            'Content-Type': 'multipart/form-data',
            'Authorization': 'Bearer ' + accessToken
        };
        const prevStateInfo = localStorage.getItem("STORAGE_KEY_PREV");
        let payload = {
            "subject": formValues.subject.trim(),
            "description": formValues.description.trim(),
            "product": productState.value,
            "special_info": {
                "path": location.pathname.toLowerCase(),
                "current_session": {...state},
                "prev_session": {...prevStateInfo},
            },
        }
        let formData = new FormData();
        formData.append('File', file);
        formData.append('Data', JSON.stringify(payload));

        axios.post(
            config.support_button.SUBMIT_CASE,
            formData,
            {headers: headers}
        )
            .then((response) => {
                if (response.status === 200) {
                    setSubmitSuccess(true);
                    setSubmitCaseId(response.data.case_id);
                    // Clear old form data
                    setFormValues({
                        'subject': '',
                        'description': '',
                    })
                    setProductState({data: productsList, value: ''})
                }
            })
            .catch((error) => {
                console.log("ERROR: Submit Support Ticket", error);
                setSubmitError(true);
            })
            .finally(()=>{
                setIsLoading(false);
            });
    }

    // Methods handling Product Number Autocomplete
    const filterData = (value) => {
        const data = productsList;
        const filter = {
            field: 'dataSuggestion',
            value: value,
            operator: 'contains',
            ignoreCase: true
        };
        return value ? filterBy(data, filter) : data;
    };
    const onChangeProduct = (e) => {
        let value = e.value;
        if (value.includes("|")) value = value.split(" |")[0];
        setProductState({
            data: filterData(value),
            value: value
        });

    }

    // File upload functions
    const onFileUpload = (event) => {
        setFile(event.target.files[0].getRawFile());
    }

    // Fetches user products when user opens form
    const fetchProducts = () => {
        let headers = {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + accessToken
        };
        axios.post(
            config.support_button.GET_USER_PRODUCTS, {},
            {headers: headers}
        )
            .then((response) => {
                if (response.status === 200) {
                    const results = response.data;
                    let newProducts = results.map(p => ({
                        ...p,
                        suggestion: `${p.product_number} | ${p.product_desc}`.length > 52 ?
                            `${p.product_number} | ${p.product_desc}`.substring(0, 52) + '...' : `${p.product_number} | ${p.product_desc}`,
                        dataSuggestion: `${p.product_number} | ${p.product_desc}`
                    }));
                    setProductsList(newProducts);
                }
            })
            .catch((error) => {
                console.log("ERROR: Get Downtime", error);
            });
    }

    // Methods for form rendering
    const onOpen = e => {
        contentRef.current.focus();
        // Reset form
        setSubmitError(false);
        setSubmitSuccess(false);
        setSubmitCaseId('');
    };
    const toggleForm = () => {
        setShowForm(!showForm);
        if (productsList.length === 0) fetchProducts();
    }

    // Multilingual text
    const subjectPlaceholderText = localization.toLanguageString(
        supportSubjectPlaceholderKey,
        mainMessages[siteLanguageDefault][supportSubjectPlaceholderKey]
    );
    const descriptionPlaceholderText = localization.toLanguageString(
        supportDescriptionPlaceholderKey,
        mainMessages[siteLanguageDefault][supportDescriptionPlaceholderKey]
    );
    const productNumberPlaceholderText = localization.toLanguageString(
        supportEnterProductNumberPlaceholderKey,
        mainMessages[siteLanguageDefault][supportEnterProductNumberPlaceholderKey]
    );

    // Traverse mainMenu tiles and grab hyperlink for success message
    let hyperlink = mainMenu.find(item => item.TITLE === "SUPPORT")?.SUBMENU.find(item => item.TITLE === "View Open Technical Support Cases")?.actionLink
    if (!hyperlink) hyperlink = config.KSP_CASES
    const AnchorText = AnchorWrap(Text);
    const viewMyOpenCases = <AnchorText
        className={'support-success-hyperlink'}
        href={hyperlink}
        data-trigger="false"
        target="_blank"
        textkey={supportSuccessMessageTwoKey}
        textdefault={mainMessages[siteLanguageDefault][supportSuccessMessageTwoKey]}
    />

    return (
        <>
            <div className={'floating-support-wrapper'}>
                <Tooltip
                    anchorElement="target"
                    appendTo={document.getElementById('btn-container')}
                    showCallout={true}
                    parentTitle={true}
                    openDelay={0}
                    position="left"
                    className={"floating-support-tooltip"}
                >
                    <div title={'Request Technical Support'}
                         id={'btn-container'}>
                        <Button
                            ref={anchor}
                            className='k-fab k-fab-solid k-fab-md k-pos-fixed k-rounded-full k-fab-solid-primary k-bottom-end floating-support-button'
                            style={{
                                 height: '4rem',
                                 width: '4rem',
                                 bottom: '1rem',
                                 right: '1.25rem'
                            }}
                            onClick={toggleForm}
                        >
                            <HeadsetIcon size={'large'}
                                         style={{fontSize: '1.8rem'}}/>
                        </Button>
                    </div>
                </Tooltip>
                <Popover
                    animate={false}
                    callout={false}
                    show={showForm}
                    anchor={anchor.current && anchor.current.element}
                    margin={{vertical: 16, horizontal: 0}}
                    onOpen={onOpen}
                    collision={{
                        vertical: 'fit',
                        horizontal: 'fit'
                    }}
                    className={'floating-support-popover'}
                >
                    { isLoading && (
                        <Loader
                            className={'k-overlay floating-support-loader'}
                            size="large"
                            type="converging-spinner"
                            themeColor="light"
                        />
                    )}
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginTop: '1.25rem',
                        marginRight: '1.25rem',
                        marginLeft: '1.25rem',
                        marginBottom: '0.875rem',
                    }}>
                        <div
                            style={{
                                marginTop: 0,
                                fontSize: '1.25rem',
                                fontWeight: 700,
                                margin: 0,
                            }}
                            className={'k-h5'}
                        >
                            <Text
                                textkey={requestTechnicalSupportKey}
                                textdefault={mainMessages[siteLanguageDefault][requestTechnicalSupportKey]}
                            />
                        </div>

                        <div style={{
                            position: 'relative',
                            width: '2rem',
                            height: '2rem'
                        }}>
                            <CloseIcon
                                onClick={() => setShowForm(false)}
                                style={{
                                    cursor: 'pointer',
                                    position: 'absolute',
                                    top: '-0.5rem',
                                    right: '-0.5rem',
                                }}
                            />
                        </div>
                    </div>
                    <div
                        style={{
                            paddingRight: '1.25rem',
                            paddingBottom: '1.25rem',
                            paddingLeft: '1.25rem',
                            width: '30rem'
                        }}
                        ref={contentRef}
                    >
                        {!submitSuccess && (
                            <div>
                                <div
                                    style={{
                                        display: 'flex',
                                        marginTop: 0,
                                        fontSize: '0.875rem',
                                        color: 'var(--keysight-dark-purple)',
                                        backgroundColor: 'var(--keysight-light-purple)',
                                        padding: '0.5rem',
                                        borderRadius: '0.5rem',
                                        marginBottom: '0.875rem',
                                    }}
                                >
                                    <div className={'k-mr-1'}>
                                        <InfoIcon
                                            size={'large'}
                                            style={{
                                                'color': 'var(--keysight-purple)'
                                            }}
                                        />
                                    </div>
                                    <div>
                                        <Text
                                            textkey={supportContactInformationKey}
                                            textdefault={mainMessages[siteLanguageDefault][supportContactInformationKey]}
                                        />
                                    </div>
                                </div>
                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                    }}
                                >
                                    <div style={{marginBottom: '0.875rem'}}>
                                        <Label>
                                            <Text
                                                textkey={supportSubjectKey}
                                                textdefault={mainMessages[siteLanguageDefault][supportSubjectKey]}
                                            />
                                            <span
                                                style={{
                                                    "color": "var(--color-alert-danger-base)",
                                                    "whiteSpace": "pre"
                                                }}
                                            > *
                                        </span>
                                        </Label>
                                        <Input
                                            name={'subject'}
                                            value={formValues.subject}
                                            maxLength={50}
                                            onChange={onChangeForm}
                                            placeholder={subjectPlaceholderText}
                                        />
                                    </div>
                                    <div>
                                        <Label>
                                            <Text
                                                textkey={supportDescriptionKey}
                                                textdefault={mainMessages[siteLanguageDefault][supportDescriptionKey]}
                                            />
                                            <span
                                                style={{
                                                    "color": "var(--color-alert-danger-base)",
                                                    "whiteSpace": "pre"
                                                }}
                                            > *
                                        </span>
                                        </Label>
                                        <TextArea
                                            name={'description'}
                                            value={formValues.description}
                                            maxLength={200}
                                            onChange={onChangeForm}
                                            resizable={'none'}
                                            placeholder={descriptionPlaceholderText}
                                            rows={3}
                                        />
                                        <div style={{
                                            fontStyle: 'italic',
                                            textAlign: 'right',
                                            fontSize: '12px'
                                        }}>
                                            {formValues.description.length} / 200
                                        </div>
                                    </div>
                                    <div style={{marginBottom: '0.875rem'}}>
                                        <Label editorId={'prod_num'}>
                                            <Text
                                                textkey={supportEnterProductNumberKey}
                                                textdefault={mainMessages[siteLanguageDefault][supportEnterProductNumberKey]}
                                            />
                                        </Label>
                                        <AutoComplete
                                            id="prod_num"
                                            textField='suggestion'
                                            data={productState.data}
                                            value={productState.value}
                                            onChange={onChangeProduct}
                                            placeholder={productNumberPlaceholderText}
                                            listNoDataRender={() => <div
                                                style={{padding: '1rem'}}>No
                                                products found.</div>}
                                        />
                                    </div>
                                    <div className={'floating-support-upload'}>
                                        <Label>
                                            <Text
                                                textkey={supportAttachmentKey}
                                                textdefault={mainMessages[siteLanguageDefault][supportAttachmentKey]}
                                            />
                                            <span style={{
                                                color: 'var(--color-text-disabled)',
                                                marginLeft: '0.5rem',
                                                fontStyle: 'italic'
                                            }}>
                                            <Text
                                                textkey={supportAttachmentDescriptionKey}
                                                textdefault={mainMessages[siteLanguageDefault][supportAttachmentDescriptionKey]}
                                            />
                                        </span>
                                        </Label>
                                        <Upload
                                            className="ksm-upload-small"
                                            batch={false}
                                            multiple={false}
                                            withCredentials={false}
                                            defaultFiles={[]}
                                            restrictions={{
                                                allowedExtensions: [
                                                    ".jpg", ".jpeg", ".png", ".gif", ".bmp", ".tiff", ".svg",
                                                    ".pdf", ".doc", ".docx", ".xls", ".xlsx", ".ppt", ".pptx",
                                                    ".txt", ".csv", ".lic", ".bin", ".log"
                                                ],
                                                maxFileSize: 10485760, // 10 MB
                                            }}
                                            saveUrl={config.utilities.health}
                                            onAdd={onFileUpload}
                                            selectMessageUI={() =>
                                                <Button
                                                    themeColor={"tertiary"}
                                                    fillMode={"solid"}
                                                    type={"button"}
                                                >
                                                    <Text
                                                        textkey={supportAttachmentButtonKey}
                                                        textdefault={mainMessages[siteLanguageDefault][supportAttachmentButtonKey]}
                                                    />
                                                </Button>
                                            }
                                        />
                                    </div>
                                </div>
                                <div
                                    style={{
                                        marginTop: '1rem',
                                        display: 'flex',
                                        justifyContent: 'end',
                                    }}
                                >
                                    <Button
                                        themeColor={"primary"}
                                        fillMode={"solid"}
                                        type={"button"}
                                        onClick={submitTicket}
                                        disabled={submitDisabled}
                                    >
                                        <Text
                                            textkey={submitRequestKey}
                                            textdefault={mainMessages[siteLanguageDefault][submitRequestKey]}
                                        />
                                    </Button>
                                </div>
                            </div>
                        )}
                        {submitError && !submitSuccess && (
                            <div className={'k-mt-2'}>
                                <Alert
                                    type={'error'}
                                    title={
                                        <Text
                                            textkey={supportErrorTitleKey}
                                            textdefault={mainMessages[siteLanguageDefault][supportErrorTitleKey]}
                                        />
                                    }
                                    message={localization.toLanguageString(supportErrorMessageKey, mainMessages[siteLanguageDefault][supportErrorMessageKey])}
                                />
                            </div>
                        )}
                        {submitSuccess && (
                            <div className={'k-mt-2'}>
                                <Alert
                                    type={'success'}
                                    title={
                                        <>
                                            {localization.toLanguageString(supportSuccessTitleKey, mainMessages[siteLanguageDefault][supportSuccessTitleKey])}
                                            <br />
                                            {localization.toLanguageString(supportSuccessTitleTwoKey, mainMessages[siteLanguageDefault][supportSuccessTitleTwoKey])}
                                            {submitCaseId}
                                        </>
                                    }
                                    message={
                                        <>
                                        {localization.toLanguageString(supportSuccessMessageOneKey, mainMessages[siteLanguageDefault][supportSuccessMessageOneKey])}
                                        {viewMyOpenCases}
                                        {localization.toLanguageString(supportSuccessMessageThreeKey, mainMessages[siteLanguageDefault][supportSuccessMessageThreeKey])}
                                        </>
                                    }
                                />
                            </div>
                        )}
                    </div>
                </Popover>
            </div>
        </>
    )
}